import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { User } from '../types'

interface FollowSystemProps {
  targetUserId: string
  targetUsername: string
  initialFollowersCount?: number
  initialFollowingCount?: number
  isFollowing?: boolean
  onFollowChange?: (isFollowing: boolean) => void
  showCounts?: boolean
  size?: 'small' | 'medium' | 'large'
}

interface FollowStats {
  followersCount: number
  followingCount: number
  isFollowing: boolean
  isFollowedBy: boolean
}

// Mock data - replace with API calls
const mockFollowStats: { [userId: string]: FollowStats } = {
  'user1': {
    followersCount: 245,
    followingCount: 89,
    isFollowing: false,
    isFollowedBy: true
  },
  'vendor1': {
    followersCount: 1567,
    followingCount: 23,
    isFollowing: true,
    isFollowedBy: false
  },
  'user2': {
    followersCount: 12,
    followingCount: 156,
    isFollowing: false,
    isFollowedBy: false
  }
}

export default function FollowSystem({
  targetUserId,
  targetUsername,
  initialFollowersCount,
  initialFollowingCount,
  isFollowing: initialIsFollowing,
  onFollowChange,
  showCounts = true,
  size = 'medium'
}: FollowSystemProps) {
  const { user, isLoggedIn } = useAuth()
  const [followStats, setFollowStats] = useState<FollowStats>({
    followersCount: initialFollowersCount || 0,
    followingCount: initialFollowingCount || 0,
    isFollowing: initialIsFollowing || false,
    isFollowedBy: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showFollowersModal, setShowFollowersModal] = useState(false)
  const [showFollowingModal, setShowFollowingModal] = useState(false)

  const isOwnProfile = user?.id === targetUserId

  useEffect(() => {
    // Load follow stats from API
    const stats = mockFollowStats[targetUserId]
    if (stats) {
      setFollowStats(stats)
    }
  }, [targetUserId])

  const handleFollow = async () => {
    if (!isLoggedIn || !user || isOwnProfile) return

    setIsLoading(true)
    try {
      const newIsFollowing = !followStats.isFollowing

      // TODO: API call to follow/unfollow user
      console.log(`${newIsFollowing ? 'Following' : 'Unfollowing'} user ${targetUserId}`)

      // Update local state
      setFollowStats(prev => ({
        ...prev,
        isFollowing: newIsFollowing,
        followersCount: prev.followersCount + (newIsFollowing ? 1 : -1)
      }))

      onFollowChange?.(newIsFollowing)

      // TODO: Create notification for followed user
      if (newIsFollowing) {
        console.log(`Creating follow notification for ${targetUserId}`)
      }

    } catch (error) {
      console.error('Error updating follow status:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getFollowButtonText = () => {
    if (isLoading) return 'Loading...'
    if (followStats.isFollowing) return 'Following'
    if (followStats.isFollowedBy) return 'Follow Back'
    return 'Follow'
  }

  const getFollowButtonClass = () => {
    let className = `follow-btn ${size}`
    if (followStats.isFollowing) className += ' following'
    if (followStats.isFollowedBy) className += ' follow-back'
    if (isLoading) className += ' loading'
    return className
  }

  return (
    <div className="follow-system">
      {/* Follow Stats */}
      {showCounts && (
        <div className="follow-stats">
          <button
            onClick={() => setShowFollowersModal(true)}
            className="stat-btn"
            title="View followers"
          >
            <span className="stat-number">{followStats.followersCount.toLocaleString()}</span>
            <span className="stat-label">Followers</span>
          </button>
          
          <button
            onClick={() => setShowFollowingModal(true)}
            className="stat-btn"
            title="View following"
          >
            <span className="stat-number">{followStats.followingCount.toLocaleString()}</span>
            <span className="stat-label">Following</span>
          </button>
        </div>
      )}

      {/* Follow Button */}
      {!isOwnProfile && isLoggedIn && (
        <div className="follow-actions">
          <button
            onClick={handleFollow}
            disabled={isLoading}
            className={getFollowButtonClass()}
          >
            <span className="follow-icon">
              {followStats.isFollowing ? '✓' : '+'}
            </span>
            <span className="follow-text">{getFollowButtonText()}</span>
          </button>

          {followStats.isFollowedBy && (
            <span className="follows-you-badge" title={`${targetUsername} follows you`}>
              Follows you
            </span>
          )}
        </div>
      )}

      {/* Mutual Followers Indicator */}
      {!isOwnProfile && (
        <MutualFollowers 
          targetUserId={targetUserId}
          currentUserId={user?.id}
        />
      )}

      {/* Followers Modal */}
      {showFollowersModal && (
        <FollowersModal
          targetUserId={targetUserId}
          targetUsername={targetUsername}
          type="followers"
          onClose={() => setShowFollowersModal(false)}
        />
      )}

      {/* Following Modal */}
      {showFollowingModal && (
        <FollowersModal
          targetUserId={targetUserId}
          targetUsername={targetUsername}
          type="following"
          onClose={() => setShowFollowingModal(false)}
        />
      )}
    </div>
  )
}

// Component for showing mutual followers
function MutualFollowers({ 
  targetUserId, 
  currentUserId 
}: { 
  targetUserId: string
  currentUserId?: string 
}) {
  const [mutualCount, setMutualCount] = useState(0)
  const [mutualUsers, setMutualUsers] = useState<User[]>([])

  useEffect(() => {
    if (!currentUserId) return

    // TODO: API call to get mutual followers
    // For now, using mock data
    const mockMutuals = [
      { id: 'mutual1', username: 'SharedFriend', role: 'member' },
      { id: 'mutual2', username: 'CommonFollower', role: 'trusted' }
    ]
    
    setMutualCount(mockMutuals.length)
    setMutualUsers(mockMutuals as User[])
  }, [targetUserId, currentUserId])

  return null
}

// Modal for showing followers/following lists
function FollowersModal({
  targetUserId,
  targetUsername,
  type,
  onClose
}: {
  targetUserId: string
  targetUsername: string
  type: 'followers' | 'following'
  onClose: () => void
}) {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true)
      try {
        // TODO: API call to get followers/following
        // Mock data for now
        const mockUsers: User[] = [
          {
            id: 'user1',
            username: 'KratomExpert',
            email: 'expert@example.com',
            dateOfBirth: new Date('1990-01-01'),
            isAgeVerified: true,
            dateCreated: new Date('2024-01-01'),
            isVerified: true,
            role: 'trusted'
          },
          {
            id: 'vendor1',
            username: 'QualityVendor',
            email: 'vendor@example.com',
            dateOfBirth: new Date('1985-01-01'),
            isAgeVerified: true,
            dateCreated: new Date('2023-06-01'),
            isVerified: true,
            role: 'verified_vendor'
          }
        ]
        
        setUsers(mockUsers)
      } catch (error) {
        console.error(`Error loading ${type}:`, error)
      } finally {
        setLoading(false)
      }
    }

    loadUsers()
  }, [targetUserId, type])

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase())
  )

  function getRoleColor(role: string): string {
    switch (role) {
      case 'admin': return '#e53e3e'
      case 'moderator': return '#9f7aea'
      case 'verified_vendor': return '#00d166'
      case 'trusted': return '#4299e1'
      default: return '#a0aec0'
    }
  }

  function getRoleBadge(role: string): string {
    switch (role) {
      case 'admin': return '👑'
      case 'moderator': return '🛡️'
      case 'verified_vendor': return '✅'
      case 'trusted': return '⭐'
      default: return ''
    }
  }

  return (
    <div className="followers-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{targetUsername}'s {type === 'followers' ? 'Followers' : 'Following'}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="modal-body">
          <div className="search-section">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="users-list">
            {loading ? (
              <div className="loading">Loading {type}...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="no-users">
                {searchQuery ? 'No users found matching your search.' : `No ${type} yet.`}
              </div>
            ) : (
              filteredUsers.map(user => (
                <div key={user.id} className="user-item">
                  <Link to={`/profile/${user.id}`} className="user-link">
                    <div className="user-avatar">
                      <div className="avatar-placeholder">
                        {user.username[0].toUpperCase()}
                      </div>
                    </div>
                    <div className="user-info">
                      <span 
                        className="username"
                        style={{ color: getRoleColor(user.role) }}
                      >
                        {getRoleBadge(user.role)} {user.username}
                      </span>
                      <span className="user-role">{user.role}</span>
                    </div>
                  </Link>
                  
                  <FollowSystem
                    targetUserId={user.id}
                    targetUsername={user.username}
                    showCounts={false}
                    size="small"
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      <div className="modal-overlay" onClick={onClose} />
    </div>
  )
}